import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { AlertTriangle, Clock, LogOut, ShieldCheck } from 'lucide-react';

const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const WARNING_THRESHOLD = 14 * 60 * 1000; // 14 minutes (60s before timeout)

export default function SessionManager() {
  const { logout, isAuthenticated } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  
  const idleTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  const resetIdleTimer = useCallback(() => {
    if (showWarning) return; // Don't reset if warning is already showing
    
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    
    idleTimerRef.current = setTimeout(() => {
      setShowWarning(true);
    }, WARNING_THRESHOLD);
  }, [showWarning]);

  const handleLogout = useCallback(() => {
    setShowWarning(false);
    logout();
    window.location.href = "/login?reason=expired";
  }, [logout]);

  const staySignedIn = () => {
    setShowWarning(false);
    setCountdown(60);
    resetIdleTimer();
  };

  // 1. Monitor user activity
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    const handleActivity = () => resetIdleTimer();

    events.forEach(event => document.addEventListener(event, handleActivity));
    resetIdleTimer();

    return () => {
      events.forEach(event => document.removeEventListener(event, handleActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [isAuthenticated, resetIdleTimer]);

  // 2. Manage countdown when warning is shown
  useEffect(() => {
    if (showWarning && countdown > 0) {
      countdownTimerRef.current = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      handleLogout();
    }

    return () => {
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, [showWarning, countdown, handleLogout]);

  if (!isAuthenticated) return null;

  return (
    <AnimatePresence>
      {showWarning && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-bg-app/80 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative bg-bg-card w-full max-w-sm shadow-floating rounded-card border border-border-light p-8 flex flex-col items-center text-center"
          >
            <div className="w-16 h-16 bg-bg-warning/20 rounded-full flex items-center justify-center mb-6">
              <Clock className="w-8 h-8 text-feedback-warning animate-pulse" />
            </div>
            
            <h2 className="text-[20px] font-bold text-text-primary tracking-tight mb-2">
              Session Expiring Soon
            </h2>
            
            <p className="text-[14px] text-text-secondary leading-relaxed mb-6">
              You have been idle for a while. For your security, you will be logged out in{" "}
              <span className="font-bold text-primary tabular-nums">{countdown}s</span>.
            </p>
            
            <div className="flex flex-col w-full gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={staySignedIn}
                className="w-full py-3 bg-primary text-white font-bold rounded-button shadow-card flex items-center justify-center gap-2"
              >
                <ShieldCheck className="w-4 h-4" />
                Keep Me Signed In
              </motion.button>
              
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleLogout}
                className="w-full py-3 bg-bg-soft text-text-secondary font-bold rounded-button hover:bg-[#FFF5F5] hover:text-[#E53E3E] transition-colors flex items-center justify-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Log Out Now
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
